import { readGithubIntegrationConfigs } from '@backstage/integration';
import {  ConfigApi, IdentityApi } from '@backstage/core-plugin-api';
import { GithubActionsApi } from '@backstage-community/plugin-github-actions';
import { Octokit, RestEndpointMethodTypes } from '@octokit/rest';


//custom tenant aware client to avoid default flow to github outh
export class TenantAwareGithubActionsClient implements GithubActionsApi {
  private readonly configApi: ConfigApi;
  private readonly identityApi: IdentityApi;

  constructor(options: { configApi: ConfigApi; identityApi: IdentityApi }) {
    this.configApi = options.configApi;
    this.identityApi = options.identityApi;
  }

  private async getOctokit(hostname: string = 'github.com'): Promise<Octokit> {
    const idToken = (await this.identityApi.getCredentials()).token
    const url =  this.configApi.getOptionalString('backend.baseUrl');
   
    const response = await fetch(`${url}/api/user/github-actions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });
    
    const {token} =  await response.json()
    if (!response.ok) {
      throw new Error('Failed to fetch GitHub token from backend');
    }

    if (!token) {
      throw new Error('No identity token available for GitHub requests');
    }

    const configs = readGithubIntegrationConfigs(
      this.configApi.getOptionalConfigArray('integrations.github') ?? [],
    );
    const githubIntegrationConfig = configs.find(v => v.host === hostname);
    const baseUrl = githubIntegrationConfig?.apiBaseUrl;

    return new Octokit({ auth: token, baseUrl });
  }

  async reRunWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<any> {
    const { hostname, owner, repo, runId } = options;
    const octokit = await this.getOctokit(hostname);
    return octokit.actions.reRunWorkflow({ owner, repo, run_id: runId });
  }

  async listWorkflowRuns(options: {
    hostname?: string;
    owner: string;
    repo: string;
    pageSize?: number;
    page?: number;
    branch?: string;
  }): Promise<
    RestEndpointMethodTypes['actions']['listWorkflowRuns']['response']['data']
  > {
    const { hostname, owner, repo, pageSize = 100, page = 0, branch } = options;
    const octokit = await this.getOctokit(hostname);
    const workflowRuns = await octokit.actions.listWorkflowRunsForRepo({
      owner,
      repo,
      per_page: pageSize,
      page,
      ...(branch ? { branch } : {}),
    });
    console.log('Workflow runs debug:', JSON.stringify(workflowRuns.data, null, 2));

    return workflowRuns.data;
  }

  async getWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }): Promise<
    RestEndpointMethodTypes['actions']['getWorkflow']['response']['data']
  > {
    const { hostname, owner, repo, id } = options;
    const octokit = await this.getOctokit(hostname);
    const workflow = await octokit.actions.getWorkflow({
      owner,
      repo,
      workflow_id: id,
    });
    return workflow.data;
  }

  async getWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }): Promise<
    RestEndpointMethodTypes['actions']['getWorkflowRun']['response']['data']
  > {
    const { hostname, owner, repo, id } = options;
    const octokit = await this.getOctokit(hostname);
    const run = await octokit.actions.getWorkflowRun({ owner, repo, run_id: id });
    return run.data;
  }

  async listJobsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
    pageSize?: number;
    page?: number;
  }): Promise<
    RestEndpointMethodTypes['actions']['listJobsForWorkflowRun']['response']['data']
  > {
    const { hostname, owner, repo, id, pageSize = 100, page = 0 } = options;
    const octokit = await this.getOctokit(hostname);
    const jobs = await octokit.actions.listJobsForWorkflowRun({
      owner,
      repo,
      run_id: id,
      per_page: pageSize,
      page,
    });
    return jobs.data;
  }

  async downloadJobLogsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<
    RestEndpointMethodTypes['actions']['downloadJobLogsForWorkflowRun']['response']['data']
  > {
    const { hostname, owner, repo, runId } = options;
    const octokit = await this.getOctokit(hostname);
    const workflow = await octokit.actions.downloadJobLogsForWorkflowRun({
      owner,
      repo,
      job_id: runId,
    });
    return workflow.data;
  }

  async listBranches(options: {
    hostname?: string;
    owner: string;
    repo: string;
    page?: number;
  }): Promise<
    RestEndpointMethodTypes['repos']['listBranches']['response']['data']
  > {
    const { hostname, owner, repo, page = 0 } = options;
    const octokit = await this.getOctokit(hostname);
    const response = await octokit.rest.repos.listBranches({
      owner,
      repo,
      per_page: 100,
      page,
    });
    return response.data;
  }

  async getDefaultBranch(options: {
    hostname?: string;
    owner: string;
    repo: string;
  }): Promise<
    RestEndpointMethodTypes['repos']['get']['response']['data']['default_branch']
  > {
    const { hostname, owner, repo } = options;
    const octokit = await this.getOctokit(hostname);
    const response = await octokit.rest.repos.get({ owner, repo });
    return response.data.default_branch;
  }
}
